import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import AdminDashboardPage from './pages/AdminDashboardPage';
import DashboardPage from './pages/DashboardPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DemoPage from './pages/DemoPage';
import TestQueriesPage from './pages/TestQueriesPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';

const SessionExpiredBanner = () => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem('session_expired')) {
            setShow(true);
            sessionStorage.removeItem('session_expired');
            setTimeout(() => setShow(false), 5000);
        }
    }, []);

    if (!show) return null;

    return (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-amber-100 border border-amber-300 text-amber-800 px-4 py-2 rounded-md shadow-lg">
            Your session expired. Please sign in again.
        </div>
    );
};

// Create Query Client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});

// Removed previous Navigation/AppLayout in favor of the new AppShell

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated, isLoading } = useAuth();
    if (isLoading) return null;
    if (!isAuthenticated) return <Navigate to="/" replace />;
    return <>{children}</>;
};

function App() {
    const { isAuthenticated, isLoading } = useAuth();

    // Show loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <QueryClientProvider client={queryClient}>
            <Toaster
              position="bottom-right"
              toastOptions={{
                className: 'bg-card border border-border text-foreground font-sans text-sm rounded shadow-sm',
                style: {
                  background: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-foreground)',
                },
              }}
            />
            <SessionExpiredBanner />
            <Router>
                <Routes>
                    {/* Home Route: guests see testing, users go to dashboard */}
                    <Route
                        path="/"
                        element={
                            // Always render HomePage, even when authenticated.
                            // This preserves the user's context when they log in from the
                            // free analysis flow so they can continue on the same screen.
                            <AppShell>
                                <HomePage />
                            </AppShell>
                        }
                    />

                    {/* Demo page - old landing with run flow, login, analysis (no link from main site) */}
                    <Route
                        path="/demo"
                        element={
                            <AppShell>
                                <DemoPage />
                            </AppShell>
                        }
                    />

                    {/* Login page (no link from main site, direct access only) */}
                    <Route
                        path="/login"
                        element={
                            <AppShell>
                                <LoginPage />
                            </AppShell>
                        }
                    />

                    {/* Test queries page (original URL input + analysis flow) */}
                    <Route
                        path="/test-queries"
                        element={
                            <AppShell>
                                <TestQueriesPage />
                            </AppShell>
                        }
                    />

                    <Route
                        path="/dashboard/*"
                        element={
                            <ProtectedRoute>
                                <AppShell>
                                    <DashboardPage />
                                </AppShell>
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/payment/success"
                        element={
                            <AppShell>
                                <PaymentSuccessPage />
                            </AppShell>
                        }
                    />

                    <Route
                        path="/privacy"
                        element={
                            <AppShell>
                                <PrivacyPolicy />
                            </AppShell>
                        }
                    />

                    <Route
                        path="/terms"
                        element={
                            <AppShell>
                                <TermsOfService />
                            </AppShell>
                        }
                    />

                    {/* Admin dashboard - standalone, no AppShell */}
                    <Route path="/admin" element={<AdminDashboardPage />} />

                    {/* Catch-all route */}
                    <Route
                        path="*"
                        element={
                            isAuthenticated ?
                                <Navigate to="/dashboard" replace /> :
                                <Navigate to="/" replace />
                        }
                    />
                </Routes>
            </Router>
        </QueryClientProvider>
    );
}

export default App;
